import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
    setCommandMessage,
    resetOperation,
    setOperationData,
    setOperationStep,
} from '../../../redux/operationSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { useInputText } from '../../../utils/useInputText';
import { COMMANDS } from '../../../utils/commonProperties';
import { createDrawingClickHandler, applyGridSnap } from "./useDrawingEvents";
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { createEscapeHandler } from '../../../utils/escapeHandler';
import { DrawCommand } from '../../../modules/undoRedo';
import { addUserEntity } from '../../../redux/dxfSlice';

const useDrawArc = (scene, camera, renderer, snapPoints) => {
    const snapMarkerRef = useRef(null);
    const tempArcRef = useRef(null);
    const inputBuffer = useRef('');
    const lastMouse = useRef({ x: 0, y: 0 });
    const dispatch = useDispatch();
    const inputText = useInputText(scene, camera);

    const { commandType, step, data, drawMethod } = useSelector(state => state.operation);
    const gridMode = useSelector(state => state.mods.gridMode);
    const objectSnap = useSelector(state => state.mods.objectSnap);
    const snapMode = useSelector((state) => state.mods.snapMode);
    const { activeLayerName } = useSelector((state) => state.layer);
    const activeTabId = useSelector(state => state.projectTabs.activeTabId);

    const serializeVector = (v) => ({ x: v.x, y: v.y, z: v.z });

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_ARC || !scene || !camera || !renderer) return;

        const domElement = renderer.domElement;
        const method = drawMethod || 'center_start_end';

        const circumscribedCircle = (p1, p2, p3) => {
            const ax = p1.x, ay = p1.y, bx = p2.x, by = p2.y, cx = p3.x, cy = p3.y;
            const D = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
            if (Math.abs(D) < 1e-10) return null;
            const ux = ((ax*ax+ay*ay)*(by-cy) + (bx*bx+by*by)*(cy-ay) + (cx*cx+cy*cy)*(ay-by)) / D;
            const uy = ((ax*ax+ay*ay)*(cx-bx) + (bx*bx+by*by)*(ax-cx) + (cx*cx+cy*cy)*(bx-ax)) / D;
            const center = new THREE.Vector3(ux, uy, 0);
            return { center, radius: center.distanceTo(p1) };
        };

        const createArcMesh = (center, radius, startAngle, endAngle, color) => {
            const angleStep = (endAngle - startAngle) / 64;
            const points = [];
            for (let i = 0; i <= 64; i++) {
                const angle = startAngle + i * angleStep;
                points.push(new THREE.Vector3(
                    center.x + radius * Math.cos(angle),
                    center.y + radius * Math.sin(angle),
                    0
                ));
            }
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const material = new THREE.LineBasicMaterial({ color });
            return new THREE.Line(geometry, material);
        };

        const removeTempArc = () => {
            if (tempArcRef.current) {
                scene.remove(tempArcRef.current);
                tempArcRef.current.geometry.dispose();
                tempArcRef.current.material.dispose();
                tempArcRef.current = null;
            }
        };

        const computeStandardArc = (center, start, end) => {
            const radius = center.distanceTo(start);
            const vStart = new THREE.Vector2(start.x - center.x, start.y - center.y);
            const vEnd = new THREE.Vector2(end.x - center.x, end.y - center.y);
            let startAngle = Math.atan2(vStart.y, vStart.x);
            let endAngle = Math.atan2(vEnd.y, vEnd.x);
            if (endAngle <= startAngle) endAngle += Math.PI * 2;
            return { center, radius, startAngle, endAngle };
        };

        const compute3PointArc = (p1, p2, p3) => {
            const result = circumscribedCircle(p1, p2, p3);
            if (!result) return null;
            const { center, radius } = result;

            const norm = (a) => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
            let sa = norm(Math.atan2(p1.y - center.y, p1.x - center.x));
            let ea = norm(Math.atan2(p3.y - center.y, p3.x - center.x));
            const ma = norm(Math.atan2(p2.y - center.y, p2.x - center.x));

            const angleCCW = (from, to) => {
                let d = to - from;
                if (d < 0) d += Math.PI * 2;
                return d;
            };

            // If p2 is NOT between p1→p3 going CCW, swap to go the other way
            if (angleCCW(sa, ma) > angleCCW(sa, ea)) {
                [sa, ea] = [ea, sa];
            }

            let endAngle = ea;
            if (endAngle <= sa) endAngle += Math.PI * 2;

            return { center, radius, startAngle: sa, endAngle };
        };

        const computeStartEndRadius = (start, end, r) => {
            const halfDist = start.distanceTo(end) / 2;
            if (r < halfDist) return null;
            const mid = start.clone().add(end).multiplyScalar(0.5);
            const h = Math.sqrt(r * r - halfDist * halfDist);
            const dx = end.x - start.x;
            const dy = end.y - start.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            const perpX = -dy / len;
            const perpY = dx / len;
            const center = new THREE.Vector3(mid.x + h * perpX, mid.y + h * perpY, 0);
            return computeStandardArc(center, start, end);
        };

        const finalizeArc = (arcData) => {
            const arc = createArcMesh(arcData.center, arcData.radius, arcData.startAngle, arcData.endAngle, 0xffffff);
            arc.userData = {
                id: generateUniqueId(),
                isSelectable: true,
                type: 'arc',
                center: { x: arcData.center.x, y: arcData.center.y, z: 0 },
                radius: arcData.radius,
                startAngle: arcData.startAngle,
                endAngle: arcData.endAngle,
            };
            scene.add(arc);

            // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
            dispatch(addUserEntity({
                type: 'ARC',
                center: { x: arcData.center.x, y: arcData.center.y, z: arcData.center.z || 0 },
                radius: arcData.radius,
                startAngle: arcData.startAngle,
                endAngle: arcData.endAngle,
                angleLength: arcData.endAngle - arcData.startAngle,
                layer: activeLayerName || '0',
                _userEntityId: arc.userData.id,
                _userDrawn: true,
                _tabId: activeTabId || 'mimari',
            }));

            // Undo-redo kaydı
            const manager = window.undoRedoManager;
            if (manager) {
                const tx = manager.beginTransaction('Draw Arc');
                manager.addCommand(new DrawCommand(arc));
                manager.commitTransaction(tx);
            }

            removeTempArc();
            inputBuffer.current = '';
            dispatch(resetOperation());
            dispatch(setCommandMessage(""));
            inputText.clear();
        };

        // Initial message
        if (method === 'center_start_end') {
            dispatch(setCommandMessage("Yay merkezi için nokta seçin"));
        } else if (method === '3point') {
            dispatch(setCommandMessage("1. noktayı seçin"));
        } else if (method === 'start_center_end') {
            dispatch(setCommandMessage("Başlangıç noktasını seçin"));
        } else if (method === 'start_end_radius') {
            dispatch(setCommandMessage("Başlangıç noktasını seçin"));
        }

        const handleArcPoint = (point) => {
            if (method === 'center_start_end') {
                if (step === 0) {
                    dispatch(setOperationData({ center: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Başlangıç noktası için tıklayın"));
                } else if (step === 1) {
                    dispatch(setOperationData({ ...data, start: serializeVector(point) }));
                    dispatch(setOperationStep(2));
                    dispatch(setCommandMessage("Bitiş noktası için tıklayın"));
                } else if (step === 2) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                    const arcData = computeStandardArc(center, start, point);
                    finalizeArc(arcData);
                }
            } else if (method === '3point') {
                if (step === 0) {
                    dispatch(setOperationData({ p1: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Ara noktayı seçin"));
                } else if (step === 1) {
                    dispatch(setOperationData({ ...data, p2: serializeVector(point) }));
                    dispatch(setOperationStep(2));
                    dispatch(setCommandMessage("Bitiş noktasını seçin"));
                } else if (step === 2) {
                    const p1 = new THREE.Vector3(data.p1.x, data.p1.y, data.p1.z);
                    const p2 = new THREE.Vector3(data.p2.x, data.p2.y, data.p2.z);
                    const arcData = compute3PointArc(p1, p2, point);
                    if (arcData) finalizeArc(arcData);
                }
            } else if (method === 'start_center_end') {
                if (step === 0) {
                    dispatch(setOperationData({ start: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Merkez noktasını seçin"));
                } else if (step === 1) {
                    dispatch(setOperationData({ ...data, center: serializeVector(point) }));
                    dispatch(setOperationStep(2));
                    dispatch(setCommandMessage("Bitiş noktasını seçin"));
                } else if (step === 2) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                    const arcData = computeStandardArc(center, start, point);
                    finalizeArc(arcData);
                }
            } else if (method === 'start_end_radius') {
                if (step === 0) {
                    dispatch(setOperationData({ start: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Bitiş noktasını seçin"));
                } else if (step === 1) {
                    dispatch(setOperationData({ ...data, end: serializeVector(point) }));
                    dispatch(setOperationStep(2));
                    dispatch(setCommandMessage("Yarıçap değeri girin"));
                }
                // step 2 is handled by keyboard Enter
            }
        };

        const handleClick = createDrawingClickHandler({
            scene,
            camera,
            renderer,
            snapPoints,
            snapMarkerRef,
            gridMode,
            objectSnap,
            snapMode,
            onPointSelected: handleArcPoint
        });

        const handleMouseMove = (e) => {
            const rect = domElement.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            lastMouse.current = { x, y };
            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            const { finalPoint, snapped, snapSource } = getSnappedPoint(
                mouseNDC,
                worldPoint,
                snapPoints,
                camera,
                renderer,
                { snapMode }
            );
            const point = (objectSnap && snapped) ? finalPoint.clone() : (gridMode ? applyGridSnap(worldPoint) : worldPoint.clone());

            // Snap marker
            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // Preview
            let arcData = null;

            if (method === 'center_start_end') {
                if (step === 2 && data.center && data.start) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                    arcData = computeStandardArc(center, start, point);
                }
            } else if (method === '3point') {
                if (step === 2 && data.p1 && data.p2) {
                    const p1 = new THREE.Vector3(data.p1.x, data.p1.y, data.p1.z);
                    const p2 = new THREE.Vector3(data.p2.x, data.p2.y, data.p2.z);
                    arcData = compute3PointArc(p1, p2, point);
                }
            } else if (method === 'start_center_end') {
                if (step === 2 && data.start && data.center) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                    arcData = computeStandardArc(center, start, point);
                }
            } else if (method === 'start_end_radius') {
                if (step === 2 && data.start && data.end && inputBuffer.current) {
                    const r = parseFloat(inputBuffer.current);
                    if (!isNaN(r) && r > 0) {
                        const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                        const end = new THREE.Vector3(data.end.x, data.end.y, data.end.z);
                        arcData = computeStartEndRadius(start, end, r);
                    }
                }
            }

            removeTempArc();
            if (arcData) {
                tempArcRef.current = createArcMesh(arcData.center, arcData.radius, arcData.startAngle, arcData.endAngle, 0xffff00);
                scene.add(tempArcRef.current);
            }

            // Input text tooltip for start_end_radius
            if (method === 'start_end_radius' && step === 2) {
                inputText.update(inputBuffer.current, lastMouse.current, {
                    backgroundColor: 0x4444ff,
                    color: 0xffffff,
                    force: true
                });
            }
        };

        const escapeHandler = createEscapeHandler({
            scene,
            dispatch,
            tempObjectRef: tempArcRef,
            snapMarkerRef,
            inputBuffer,
            inputText,
            additionalCleanup: () => {
                dispatch(setCommandMessage(""));
            }
        });

        const updateRadiusPreview = () => {
            const r = parseFloat(inputBuffer.current);
            removeTempArc();
            if (!isNaN(r) && r > 0 && data.start && data.end) {
                const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                const end = new THREE.Vector3(data.end.x, data.end.y, data.end.z);
                const arcData = computeStartEndRadius(start, end, r);
                if (arcData) {
                    tempArcRef.current = createArcMesh(arcData.center, arcData.radius, arcData.startAngle, arcData.endAngle, 0xffff00);
                    scene.add(tempArcRef.current);
                }
            }
            inputText.update(inputBuffer.current, lastMouse.current, {
                backgroundColor: 0x4444ff,
                color: 0xffffff,
                force: true
            });
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                escapeHandler(e);
            } else if (method === 'start_end_radius' && step === 2) {
                if (e.key === 'Enter' && inputBuffer.current) {
                    const r = parseFloat(inputBuffer.current);
                    if (!isNaN(r) && r > 0 && data.start && data.end) {
                        const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                        const end = new THREE.Vector3(data.end.x, data.end.y, data.end.z);
                        const arcData = computeStartEndRadius(start, end, r);
                        if (arcData) finalizeArc(arcData);
                    }
                } else if (/\d|\./.test(e.key)) {
                    inputBuffer.current += e.key;
                    updateRadiusPreview();
                } else if (e.key === 'Backspace') {
                    inputBuffer.current = inputBuffer.current.slice(0, -1);
                    updateRadiusPreview();
                }
            }
        };

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, step, data, scene, camera, renderer, dispatch, gridMode, snapMode, objectSnap, drawMethod]);
};

export default useDrawArc;
